import React, { useMemo, useRef } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { create } from 'zustand';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store/auth';
import { useModalStore } from '@/store/modal';
import { usePluginStore, readPluginState } from '@/lib/plugins/pluginStore';
import { pluginRuntime } from '@/lib/plugins/portable.cjs';
import { isMacAppStore } from '@/lib/appDistribution';
import { PluginMark } from './PluginControls';

export const usePluginDirectoryNavigation = create(set => ({
  view: 'discover',
  setView: view => { if (['discover', 'installed', 'connections'].includes(view)) set({ view }); },
}));

export default function PluginDirectorySidebar({ onNavigate }) {
  const { colors } = useTheme();
  const user = useAuthStore(state => state.user);
  const openModal = useModalStore(state => state.actions.openModal);
  const revision = usePluginStore(state => state.revision);
  const view = usePluginDirectoryNavigation(state => state.view);
  const setView = usePluginDirectoryNavigation(state => state.setView);
  const sequence = useRef(0);
  const userId = user?.id && !user.flags?.isGhost ? String(user.id) : null;
  const saved = useMemo(() => {
    if (!userId) return { records: [] };
    try { return readPluginState(userId); } catch { return { records: [] }; }
  }, [userId, revision]);
  const records = (saved.records || []).filter(record =>
    (Platform.OS === 'macos' && !isMacAppStore()) || pluginRuntime(record.package) !== 'mac-browser');
  return <ScrollView style={{ flex: 1, minHeight: 0 }} contentContainerStyle={{ padding: 16, gap: 4 }}>
    <Text size="l" weight="medium" style={{ paddingHorizontal: 10, paddingTop: 8, paddingBottom: 20 }}>Customize</Text>
    {[['discover', 'Discover', 'grid-2'], ['installed', 'Installed', 'check'], ['connections', 'Connections', 'link']].map(([id, name, icon]) =>
      <Pressable key={id} accessibilityRole="button" accessibilityLabel={`Show ${name.toLowerCase()} plugins`}
        accessibilityState={{ selected: view === id }} onPress={() => { setView(id); onNavigate?.(); }}
        style={({ hovered }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 40, paddingHorizontal: 12,
          borderRadius: 9, backgroundColor: view === id || hovered ? colors.solid_color_secondary : 'transparent' })}>
        <Icon name={icon} size={16} color={colors.text_color_primary} /><Text size="s">{name}</Text>
      </Pressable>)}
    {!!userId && <Text size="xs" color="secondary" style={{ paddingHorizontal: 12, paddingTop: 28, paddingBottom: 8 }}>Installed</Text>}
    {records.map(record => {
      const entry = record.source === 'bundled' && saved.catalog?.find(item => item.id === record.package.manifest.name);
      const name = entry?.name || record.package.manifest.name;
      return <Pressable key={record.id} accessibilityRole="button" accessibilityLabel={`Open ${name} plugin`}
        onPress={() => {
          openModal('PluginDetailsModal', { userId, key: `${userId}:sidebar:${++sequence.current}`,
            initialDetail: { package: record.package, source: record.source } });
          onNavigate?.();
        }} style={({ hovered }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 38,
          paddingHorizontal: 12, paddingVertical: 6, borderRadius: 9, backgroundColor: hovered ? colors.solid_color_secondary : 'transparent' })}>
        <PluginMark entry={entry} size={20} /><Text size="s" numberOfLines={1} style={{ flex: 1 }}>{name}</Text>
        {!record.enabled && <Text size="xs" color="tertiary">Off</Text>}
      </Pressable>;
    })}
    {!!userId && !records.length && <Text size="s" color="secondary" style={{ padding: 12 }}>Your installed plugins appear here.</Text>}
  </ScrollView>;
}
